/**
 * deposito.js - GALPAO 12, centro de distribuicao.
 *
 * IDENTIDADE: mapa FECHADO. Nao ha ceu, nao ha rua: o mundo inteiro cabe dentro
 * de um galpao de 66 x 66 m com 12 m de altura livre. O que desenha o combate
 * sao as seis fileiras duplas de porta-palete de 5,6 m - paredes opacas que
 * deixam ver por baixo das prateleiras - e as duas transversais que cortam as
 * fileiras. Combate curto, de esquina, quase nunca acima de 15 m.
 *
 * PLANTA (visto de cima, +X leste, +Z norte)
 *
 *   z=+33 ##################### DOCAS DE CAMINHAO ###################
 *   z=+24 |  plataforma de doca +1,05 m (4 portas de enrolar)        |
 *         |  rampa                                     rampa        |
 *   z=+22 | MEZANINO |=== fileira 6 ===|==|=== fileira 6 ===|== | E  |
 *         | (oeste,  |                 |  |                 |    | S  |
 *   z=+12 | laje 4,5)| fileira 5       |tr|  fileira 5      |    | C  |
 *         | ESCRITO- |-----------------|an|-----------------|    | O  |
 *   z=+1  | RIO      | PASSARELA sobre o corredor central (4,5 m)   |
 *         | envidra- |-----------------|ve|-----------------|    | A  |
 *   z=-11 | cado no  | fileira 2       |rs|  fileira 2      |    | D  |
 *         | alto     |                 |al|                 |    | A  |
 *   z=-22 |==========|=== fileira 1 ===|==|=== fileira 1 ===|====|====|
 *   z=-25 |                        SALA DE MAQUINAS (laje 3,7 m)     |
 *   z=-33 ###########################################################
 *
 * VERTICALIDADE: escada dupla para o mezanino (4,5 m) -> escritorio envidracado
 * -> passarela metalica de 51 m sobre o corredor central, com as prateleiras
 * subindo 1,1 m acima do piso dela dos dois lados -> plataforma leste (4,5 m) ->
 * escada para o corredor de servico. Fora disso: a laje da sala de maquinas
 * (3,7 m) e a plataforma de doca (1,05 m).
 *
 * COBERTURA: em cada corredor entram tres cargas paletizadas de 2,5 m encostadas
 * ALTERNADAMENTE na fileira do norte e na do sul. Como cada carga e mais larga
 * que metade do corredor, nenhuma reta paralela ao corredor atravessa o galpao -
 * era o unico jeito de um vao de 66 m nao virar linha de tiro de ponta a ponta.
 */

import * as P from '../Props.js';

// ---------------------------------------------------------------------------
// coordenadas
// ---------------------------------------------------------------------------

const MINX = -33, MAXX = 33, MINZ = -33, MAXZ = 33;
const WT = 0.8;                       // espessura da parede
const IN_X0 = MINX + WT, IN_X1 = MAXX - WT;   // faces internas
const IN_Z0 = MINZ + WT, IN_Z1 = MAXZ - WT;
const H_HALL = 12.0;                  // pe direito livre
const Y_FLOOR = 0.0;

// mezanino (oeste) e escritorio no alto
const MEZ_X0 = IN_X0, MEZ_X1 = -24.0, MEZ_Z0 = -22.0, MEZ_Z1 = 22.0, MEZ_Y = 4.5;
const OFF_X0 = IN_X0, OFF_X1 = -25.0, OFF_Z0 = 10.0, OFF_Z1 = 21.6;
const OFF_H = MEZ_Y + 2.8, OFF_TOP = OFF_H + 0.3;

// plataforma de doca (norte)
const DOCK_Z0 = 24.0, Y_DOCK = 1.05;

// sala de maquinas (sudeste)
const MAQ_X0 = 6.0, MAQ_X1 = 20.0, MAQ_Z1 = -25.0, MAQ_H = 3.4, MAQ_TOP = 3.7;

// corredor de servico leste e plataforma da passarela
const SRV_X0 = 30.0;
const PLAT_X0 = 27.0, PLAT_X1 = 32.1, PLAT_Z0 = -2.0, PLAT_Z1 = 4.0;

// porta-palete
const RACK_H = 5.6;                   // altura do quadro
const RACK_D = 1.1;                   // profundidade de um lado
/** Centro Z de cada fileira DUPLA (dois quadros costa a costa). */
const RACK_Z = [-18.0, -10.5, -3.0, 4.5, 12.0, 19.5];
/** Trechos em X: as duas transversais ficam nos vaos que sobram. */
const RACK_SEG = [[-22.0, -4.0], [1.0, 14.0], [17.0, 30.0]];
const CROSS_X = [-1.5, 15.5];         // eixo das duas transversais

const CATWALK_Y = MEZ_Y;
const CATWALK_Z = 0.75;               // eixo do corredor central

export const meta = {
  id: 'deposito',
  name: 'DEP\u00d3SITO',
  sub: 'GALP\u00c3O 12 - CENTRO DE DISTRIBUI\u00c7\u00c3O',
  bounds: { minX: MINX, maxX: MAXX, minZ: MINZ, maxZ: MAXZ },
  ambiente: 'dia'
};

// ---------------------------------------------------------------------------
// ajudantes locais
// ---------------------------------------------------------------------------

/**
 * Rampa escalonada. Degrau de no maximo 0,3 m - o jogador sobe andando
 * (STEP_HEIGHT e 0,45 m), entao nao precisa de plano inclinado de verdade.
 */
function stepRamp(level, axis, a0, a1, b0, b1, yHigh, yLow, steps) {
  const n = steps || 4;
  const da = (a1 - a0) / n;
  const dy = (yHigh - yLow) / n;
  for (let i = 0; i < n; i++) {
    const s0 = a0 + da * i;
    const s1 = a0 + da * (i + 1);
    const top = yHigh - dy * i;
    if (axis === 'x') {
      level._slab('concrete', Math.min(s0, s1), yLow, b0, Math.max(s0, s1), top, b1);
    } else {
      level._slab('concrete', b0, yLow, Math.min(s0, s1), b1, top, Math.max(s0, s1));
    }
  }
}

/**
 * Um quadro de porta-palete: montantes, longarinas nos tres niveis e piso de
 * prateleira. O piso e solido de proposito - e o que da a "parede opaca com fenda
 * embaixo" que faz o combate do galpao ser de esquina.
 */
function rackFrame(level, x0, x1, zc, seed) {
  const d0 = zc - RACK_D * 0.5;
  const d1 = zc + RACK_D * 0.5;
  const rng = P.makeRng(seed);

  // montantes a cada 2,7 m, com travamento em X visivel
  const n = Math.max(2, Math.round((x1 - x0) / 2.7));
  for (let i = 0; i <= n; i++) {
    const x = x0 + ((x1 - x0) * i) / n;
    level._slab('metal', x - 0.06, 0, d0, x + 0.06, RACK_H, d0 + 0.09);
    level._slab('metal', x - 0.06, 0, d1 - 0.09, x + 0.06, RACK_H, d1);
    // sapata de piso
    level._slab('metal_thin', x - 0.13, 0, d0 - 0.03, x + 0.13, 0.12, d1 + 0.03,
      { solid: false });
    // travessas horizontais entre os dois montantes do mesmo poste
    for (const ty of [1.2, 3.6]) {
      level._slab('metal_thin', x - 0.035, ty, d0 + 0.06, x + 0.035, ty + 0.07, d1 - 0.06,
        { solid: false });
    }
  }

  // longarinas e piso das prateleiras
  for (let lv = 0; lv < 3; lv++) {
    const y = 1.5 + lv * 1.35;
    level._slab('metal', x0, y, d0, x1, y + 0.13, d0 + 0.11);
    level._slab('metal', x0, y, d1 - 0.11, x1, y + 0.13, d1);
    level._slab('wood', x0 + 0.05, y + 0.13, d0 + 0.02, x1 - 0.05, y + 0.2, d1 - 0.02,
      { cast: false });
  }
  // travessa de topo, que amarra o quadro
  level._slab('metal', x0, RACK_H - 0.14, d0, x1, RACK_H, d0 + 0.11, { solid: false });
  level._slab('metal', x0, RACK_H - 0.14, d1 - 0.11, x1, RACK_H, d1, { solid: false });

  // carga: nem toda posicao ocupada - vao vazio e o que deixa ver o outro lado
  const step = 1.35;
  for (let lv = 0; lv < 3; lv++) {
    const y = 1.5 + lv * 1.35 + 0.2;
    for (let x = x0 + 0.9; x < x1 - 0.9; x += step) {
      const r = rng();
      if (r < 0.3) continue;
      if (r < 0.62) {
        level._place(P.makePallet(level.mats), x, y, zc, (lv % 2) * Math.PI * 0.5);
        level._place(P.makeCrate(level.mats, { w: 0.8, h: 0.62, d: 0.8, variant: lv % 2 }),
          x, y + 0.11, zc, P.rand(rng, -0.2, 0.2));
      } else if (r < 0.84) {
        level._slab('wood', x - 0.55, y, zc - 0.5, x + 0.55, y + 0.95, zc + 0.5);
        level._slab('metal_thin', x - 0.58, y + 0.3, zc - 0.52, x + 0.58, y + 0.36, zc + 0.52,
          { solid: false, cast: false });
      } else {
        for (let k = 0; k < 2; k++) {
          level._place(P.makeBarrel(level.mats, { variant: 0 }),
            x - 0.28 + k * 0.56, y, zc + P.rand(rng, -0.2, 0.2), rng() * 3.1);
        }
      }
    }
  }
}

/**
 * Carga paletizada encostada numa fileira. E o bloqueio de linha de tiro: 3,6 m
 * de largura num corredor de 5,1 m, alternando o lado, fecha toda reta paralela
 * ao corredor.
 */
function cargoBlock(level, cx, cz, seed) {
  const w = 3.6, d = 1.15, h = 2.5;
  const x0 = cx - w * 0.5, x1 = cx + w * 0.5;
  const z0 = cz - d * 0.5, z1 = cz + d * 0.5;
  const rng = P.makeRng(seed);
  // palete e a massa embalada
  level._slab('wood', x0, 0, z0, x1, 0.15, z1);
  level._slab('wood', x0 + 0.06, 0.15, z0 + 0.05, x1 - 0.06, h, z1 - 0.05);
  // cintas de amarracao
  for (const fy of [0.6, 1.5, 2.3]) {
    level._slab('metal_thin', x0 + 0.02, fy, z0 + 0.01, x1 - 0.02, fy + 0.05, z1 - 0.01,
      { solid: false, cast: false });
  }
  // caixotes soltos por cima e no pe: sem eles a carga le como caixa lisa
  level._place(P.makeCrate(level.mats, { variant: 1 }), cx - 0.9, h, cz, P.rand(rng, -0.3, 0.3));
  level._place(P.makeCrate(level.mats, { variant: 3, w: 0.7, h: 0.58, d: 0.7 }),
    cx + 0.8, h, cz + 0.1, P.rand(rng, -0.4, 0.4));
  level._place(P.makeCrate(level.mats, { variant: 2 }), x1 + 0.55, 0, cz, P.rand(rng, -0.5, 0.5));
}

// ---------------------------------------------------------------------------
// construcao
// ---------------------------------------------------------------------------

export function build(level) {
  buildShell(level);
  buildRoof(level);
  buildRacks(level);
  buildMezzanine(level);
  buildOffice(level);
  buildCatwalk(level);
  buildDocks(level);
  buildMachineRoom(level);
  buildFloorDetail(level);
  buildOutside(level);
  return true;
}

/** Casca fechada: piso, quatro paredes e pilares do vao. */
function buildShell(level) {
  const b = level.batch;

  b.setRegion('dep_borda');
  // terreno externo: se algum vao de telhado deixar ver para fora, tem chao la
  level._floor('gravel', MINX - 20, MINZ - 20, MAXX + 20, MAXZ + 20, -0.02, 1.2);

  b.setRegion('dep_piso');
  level._floor('concrete', MINX, MINZ, MAXX, MAXZ, Y_FLOOR, 0.6);

  b.setRegion('dep_borda');
  // paredes: alvenaria embaixo, chapa metalica em cima (o corte e a 3,2 m)
  const walls = [
    [MINX, MINZ, IN_X0, MAXZ], [IN_X1, MINZ, MAXX, MAXZ],
    [IN_X0, MINZ, IN_X1, IN_Z0], [IN_X0, IN_Z1, IN_X1, MAXZ]
  ];
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    level._slab('concrete', w[0], -0.6, w[1], w[2], 3.2, w[3]);
    level._slab('metal_thin', w[0], 3.2, w[1], w[2], H_HALL, w[3]);
    // friso que marca a emenda dos dois materiais
    level._slab('metal', w[0] - 0.06, 3.2, w[1] - 0.06, w[2] + 0.06, 3.42, w[3] + 0.06,
      { solid: false });
  }
  // rodape de impacto: a faixa que todo galpao tem, amassada pelo empilhadeira
  const kick = { solid: false, cast: false };
  level._slab('metal_dull', IN_X0, 0, IN_Z0, IN_X0 + 0.06, 0.55, IN_Z1, kick);
  level._slab('metal_dull', IN_X1 - 0.06, 0, IN_Z0, IN_X1, 0.55, IN_Z1, kick);
  level._slab('metal_dull', IN_X0, 0, IN_Z0, IN_X1, 0.55, IN_Z0 + 0.06, kick);
  level._slab('metal_dull', IN_X0, 0, IN_Z1 - 0.06, IN_X1, 0.55, IN_Z1, kick);

  // pilares do vao: grade de 12 m, com capitel
  for (let ix = -12; ix <= 24; ix += 12) {
    for (let iz = -18; iz <= 18; iz += 12) {
      level._slab('concrete', ix - 0.3, 0, iz - 0.3, ix + 0.3, H_HALL, iz + 0.3);
      level._slab('concrete', ix - 0.42, H_HALL - 0.9, iz - 0.42, ix + 0.42, H_HALL, iz + 0.42,
        { solid: false });
      level._slab('metal_dull', ix - 0.36, 0, iz - 0.36, ix + 0.36, 0.7, iz + 0.36,
        { solid: false });
    }
  }
}

/**
 * Cobertura: tesouras, telha e as CLARABOIAS. Sem elas o galpao fechado fica
 * escuro no meio - a luz do sol so entra por aqui, e e ela que da o feixe
 * inclinado que separa "galpao" de "caixa preta".
 */
function buildRoof(level) {
  const b = level.batch;
  b.setRegion('dep_teto');

  const y0 = H_HALL, y1 = H_HALL + 0.34;
  // baias de telha ao longo de Z; duas delas viram clarabaia
  const bay = 5.5;
  let idx = 0;
  for (let z = IN_Z0; z < IN_Z1 - 0.01; z += bay) {
    const z2 = Math.min(z + bay, IN_Z1);
    const light = (idx % 3) === 1;
    if (light) {
      // clarabaia: vidro na faixa central da baia, telha nas bordas
      const m0 = z + 1.0;
      const m1 = z2 - 1.0;
      level._slab('metal_thin', IN_X0, y0, z, IN_X1, y1, m0);
      level._slab('metal_thin', IN_X0, y0, m1, IN_X1, y1, z2);
      for (let x = IN_X0 + 2.0; x < IN_X1 - 3.0; x += 8.0) {
        const x2 = Math.min(x + 5.2, IN_X1 - 2.0);
        level._slab('glass', x, y0 + 0.06, m0, x2, y0 + 0.2, m1, { cast: false });
        // moldura da clarabaia
        level._slab('metal_dull', x - 0.16, y0, m0 - 0.16, x, y1 + 0.1, m1 + 0.16,
          { solid: false });
        level._slab('metal_dull', x2, y0, m0 - 0.16, x2 + 0.16, y1 + 0.1, m1 + 0.16,
          { solid: false });
        // trecho de telha entre uma clarabaia e a proxima
        level._slab('metal_thin', x2 + 0.16, y0, m0, Math.min(x2 + 2.8, IN_X1), y1, m1);
      }
      level._slab('metal_thin', IN_X0, y0, m0, IN_X0 + 2.0, y1, m1);
    } else {
      level._slab('metal_thin', IN_X0, y0, z, IN_X1, y1, z2);
    }
    // tesoura no eixo da baia
    const tz = (z + z2) * 0.5;
    level._slab('metal', IN_X0, y0 - 1.2, tz - 0.14, IN_X1, y0 - 0.95, tz + 0.14,
      { solid: false });
    level._slab('metal', IN_X0, y0 - 0.32, tz - 0.14, IN_X1, y0 - 0.07, tz + 0.14,
      { solid: false });
    for (let x = IN_X0 + 1.5; x < IN_X1 - 1; x += 3.0) {
      level._slab('metal_thin', x - 0.05, y0 - 0.95, tz - 0.06, x + 0.05, y0 - 0.32, tz + 0.06,
        { solid: false, cast: false });
    }
    idx++;
  }
  // terca longitudinal, amarrando as tesouras
  for (let x = IN_X0 + 4; x < IN_X1 - 3; x += 8) {
    level._slab('metal_thin', x - 0.08, y0 - 0.62, IN_Z0, x + 0.08, y0 - 0.42, IN_Z1,
      { solid: false, cast: false });
  }

  // luminarias industriais, em fila por corredor
  for (let i = 0; i < RACK_Z.length; i++) {
    const z = RACK_Z[i] + 3.75;
    if (z > IN_Z1 - 2) continue;
    for (let x = -26; x <= 26; x += 13) {
      level._place(P.makeHangingLamp(level.mats, { len: 2.4 }), x, y0 - 0.7, z, 0);
    }
  }
  for (let x = -26; x <= 26; x += 13) {
    level._place(P.makeHangingLamp(level.mats, { len: 2.4 }), x, y0 - 0.7, -22.5, 0);
  }
}

/** As seis fileiras duplas e as cargas que quebram os corredores. */
function buildRacks(level) {
  const b = level.batch;
  b.setRegion('dep_estoque');

  for (let r = 0; r < RACK_Z.length; r++) {
    const zc = RACK_Z[r];
    for (let s = 0; s < RACK_SEG.length; s++) {
      const seg = RACK_SEG[s];
      rackFrame(level, seg[0], seg[1], zc - RACK_D * 0.55, r * 31 + s * 7 + 3);
      rackFrame(level, seg[0], seg[1], zc + RACK_D * 0.55, r * 31 + s * 7 + 101);
    }
    // placa de identificacao da rua, na cabeceira de cada fileira
    level._place(P.makeSign(level.mats, { w: 1.6, h: 0.5 }), seg0Head(), 3.4, zc, Math.PI / 2);
  }

  /**
   * Bloqueios dos corredores. `side` -1 encosta na fileira do sul, +1 na do
   * norte. Cada corredor recebe tres, com o lado alternado - e isso que fecha a
   * reta de 66 m.
   */
  const aisles = [
    { z0: -22.0 + RACK_D, z1: RACK_Z[0] - 1.2, xs: [-14, 8, 24], side: [-1, 1, -1] },
    { z0: RACK_Z[0] + 1.2, z1: RACK_Z[1] - 1.2, xs: [-18, 5, 22], side: [1, -1, 1] },
    { z0: RACK_Z[1] + 1.2, z1: RACK_Z[2] - 1.2, xs: [-12, 9, 25], side: [-1, 1, -1] },
    { z0: RACK_Z[3] + 1.2, z1: RACK_Z[4] - 1.2, xs: [-16, 7, 23], side: [1, -1, 1] },
    { z0: RACK_Z[4] + 1.2, z1: RACK_Z[5] - 1.2, xs: [-11, 10, 26], side: [-1, 1, -1] }
  ];
  for (let i = 0; i < aisles.length; i++) {
    const a = aisles[i];
    for (let k = 0; k < a.xs.length; k++) {
      const cz = a.side[k] < 0 ? a.z0 + 0.6 : a.z1 - 0.6;
      cargoBlock(level, a.xs[k], cz, i * 13 + k + 5);
    }
  }
  // o corredor central (onde corre a passarela) recebe bloqueio mais baixo, para
  // nao tapar a vista de quem esta em cima
  const rng = P.makeRng(8181);
  for (const cx of [-19, 6, 24]) {
    level._place(P.makePallet(level.mats), cx, Y_FLOOR, CATWALK_Z - 1.4, 0);
    level._place(P.makeCrate(level.mats, { variant: 0 }), cx, Y_FLOOR + 0.11, CATWALK_Z - 1.4, 0.3);
    level._place(P.makeCrate(level.mats, { variant: 1, w: 0.78, h: 0.6, d: 0.78 }),
      cx + 0.1, Y_FLOOR + 0.83, CATWALK_Z - 1.35, 0.7);
    level._place(P.makeDumpster(level.mats, {}), cx + 2.4, Y_FLOOR, CATWALK_Z + 1.5,
      rng() * 0.4);
  }
}

/** Cabeceira oeste das fileiras - so para a placa de rua nao ficar solta. */
function seg0Head() {
  return RACK_SEG[0][0] - 0.7;
}

/** Mezanino oeste: laje de 4,5 m, duas escadas e guarda-corpo. */
function buildMezzanine(level) {
  const b = level.batch;
  b.setRegion('dep_mezanino');

  // laje e viga de borda
  level._slab('concrete', MEZ_X0, MEZ_Y - 0.3, MEZ_Z0, MEZ_X1, MEZ_Y, MEZ_Z1);
  level._slab('metal', MEZ_X1 - 0.22, MEZ_Y - 0.62, MEZ_Z0, MEZ_X1, MEZ_Y - 0.3, MEZ_Z1);
  // pilares de apoio
  for (let z = MEZ_Z0 + 2.5; z < MEZ_Z1; z += 6.0) {
    level._slab('metal', MEZ_X1 - 0.42, 0, z - 0.14, MEZ_X1 - 0.14, MEZ_Y - 0.62, z + 0.14);
    level._slab('metal_thin', MEZ_X1 - 0.62, 0, z - 0.3, MEZ_X1 - 0.02, 0.14, z + 0.3,
      { solid: false });
    // mao-francesa
    level._slab('metal_thin', MEZ_X1 - 1.5, MEZ_Y - 1.1, z - 0.06, MEZ_X1 - 0.4, MEZ_Y - 0.62,
      z + 0.06, { solid: false });
  }

  /**
   * Guarda-corpo da borda leste, com falha nos dois desembarques de escada e na
   * boca da passarela. Falha aberta e o que deixa passar; guarda-corpo inteiro
   * transformaria a laje numa sala sem saida.
   */
  const gaps = [[-15.6, -12.4], [6.4, 9.6], [CATWALK_Z - 1.0, CATWALK_Z + 1.0]];
  let cz = MEZ_Z0 + 0.2;
  const edges = [];
  for (let i = 0; i < gaps.length; i++) {
    if (gaps[i][0] > cz + 0.6) edges.push([cz, gaps[i][0]]);
    cz = Math.max(cz, gaps[i][1]);
  }
  if (cz < MEZ_Z1 - 0.2) edges.push([cz, MEZ_Z1 - 0.2]);
  for (let i = 0; i < edges.length; i++) {
    const len = edges[i][1] - edges[i][0];
    if (len < 0.8) continue;
    level._place(P.makeRailing(level.mats, { len, h: 1.05, mesh: true }),
      MEZ_X1 - 0.1, MEZ_Y, (edges[i][0] + edges[i][1]) * 0.5, Math.PI / 2);
  }

  // duas escadas, subindo para OESTE (rotY -PI/2 leva o +Z da escada para -X)
  const steps = 13;
  const rise = MEZ_Y / steps;
  const run = 0.34;
  const reach = steps * run + 1.2;      // degraus + patamar
  for (const sz of [-14.0, 8.0]) {
    level._place(P.makeStairs(level.mats, { steps, rise, run, w: 1.6, landing: 1.2 }),
      MEZ_X1 + reach, Y_FLOOR, sz, -Math.PI / 2);
  }

  // uso do mezanino: expedicao leve
  const rng = P.makeRng(707);
  for (let i = 0; i < 5; i++) {
    const z = MEZ_Z0 + 2.5 + i * 4.4;
    level._place(P.makeShelf(level.mats, { w: 2.4, h: 2.2, d: 0.7 }),
      MEZ_X0 + 0.6, MEZ_Y, z, Math.PI / 2);
    level._place(P.makeCrate(level.mats, { variant: i % 3, w: 0.78, h: 0.6, d: 0.78 }),
      MEZ_X0 + 1.9, MEZ_Y + 1.35, z, P.rand(rng, -0.3, 0.3));
  }
  level._place(P.makeWorkbench(level.mats, { w: 2.6 }), -27.0, MEZ_Y, -19.0, 0);
  level._place(P.makeSandbagWall(level.mats, { len: 3.4, rows: 3, rng: P.makeRng(708) }),
    MEZ_X1 - 1.2, MEZ_Y, -6.0, Math.PI / 2);
  level._place(P.makeSandbagWall(level.mats, { len: 2.8, rows: 3, rng: P.makeRng(709) }),
    MEZ_X1 - 1.2, MEZ_Y, 3.0, Math.PI / 2);
  level._place(P.makeTarpStack(level.mats, { count: 2, rng: P.makeRng(710) }),
    -29.0, MEZ_Y, 0.0, 0.3);
  level._interiorDust(MEZ_X0, MEZ_Z0, MEZ_X1, MEZ_Z1, MEZ_Y, 1.0);
}

/**
 * Escritorio envidracado sobre o mezanino. O vidro corrido nas duas faces
 * viradas para o galpao e o que faz dele um posto: ve tudo, protege pouco.
 */
function buildOffice(level) {
  const b = level.batch;
  b.setRegion('dep_mezanino');
  const t = 0.24;
  const y = MEZ_Y;

  // fundo e face norte cegos (encostam na parede do galpao)
  level._slab('plaster', OFF_X0, y, OFF_Z0, OFF_X0 + t, OFF_H, OFF_Z1);
  level._slab('plaster', OFF_X0, y, OFF_Z1 - t, OFF_X1, OFF_H, OFF_Z1);
  // face leste: peitoril, vidro corrido, viga
  level._slab('plaster', OFF_X1 - t, y, OFF_Z0 + t, OFF_X1, y + 0.95, OFF_Z1 - t);
  level._slab('glass', OFF_X1 - 0.16, y + 0.95, OFF_Z0 + t, OFF_X1 - 0.05, y + 2.25,
    OFF_Z1 - t, { cast: false });
  level._slab('plaster', OFF_X1 - t, y + 2.25, OFF_Z0 + t, OFF_X1, OFF_H, OFF_Z1 - t);
  for (let z = OFF_Z0 + 3.0; z < OFF_Z1 - 1.5; z += 3.0) {
    level._slab('metal_dull', OFF_X1 - 0.19, y + 0.95, z - 0.05, OFF_X1 - 0.02, y + 2.25,
      z + 0.05, { solid: false });
  }
  // face sul: porta e mais vidro
  level._slab('plaster', OFF_X0 + t, y, OFF_Z0, -29.4, OFF_H, OFF_Z0 + t);
  level._slab('plaster', -29.4, y + 2.2, OFF_Z0, -28.0, OFF_H, OFF_Z0 + t);
  level._slab('plaster', -28.0, y, OFF_Z0, OFF_X1, y + 0.95, OFF_Z0 + t);
  level._slab('glass', -27.9, y + 0.95, OFF_Z0 + 0.05, OFF_X1 - 0.1, y + 2.25, OFF_Z0 + 0.16,
    { cast: false });
  level._slab('plaster', -28.0, y + 2.25, OFF_Z0, OFF_X1, OFF_H, OFF_Z0 + t);
  level._place(P.makeDoorUnit(level.mats, { w: 1.3, h: 2.2, style: 'metal', ajar: 0.75, open: true }),
    -28.7, y, OFF_Z0 + t * 0.5, Math.PI);

  // laje de cobertura do escritorio
  level._slab('concrete', OFF_X0 - 0.12, OFF_H, OFF_Z0 - 0.12, OFF_X1 + 0.12, OFF_TOP,
    OFF_Z1 + 0.12);
  level._place(P.makeAC(level.mats, {}), OFF_X1 - 0.4, OFF_H - 1.0, OFF_Z0 + 4.0, Math.PI / 2);
  level._place(P.makeRoofVent(level.mats, {}), -29.0, OFF_TOP, OFF_Z0 + 6.0, 0);

  // interior
  level._place(P.makeWorkbench(level.mats, { w: 2.6 }), -28.6, y, OFF_Z1 - 1.6, Math.PI);
  level._place(P.makeShelf(level.mats, { w: 2.0, h: 1.9, d: 0.55 }), OFF_X0 + 1.4, y,
    OFF_Z0 + 3.0, Math.PI / 2);
  level._place(P.makeHangingLamp(level.mats, { len: 0.8 }), -28.6, OFF_H - 0.1,
    (OFF_Z0 + OFF_Z1) * 0.5, 0);
  level._place(P.makeCrate(level.mats, { variant: 2 }), OFF_X0 + 0.9, y, OFF_Z1 - 1.2, 0.4);
}

/**
 * Passarela metalica de 51 m sobre o corredor central, em quatro tramos.
 * Quatro em vez de um so porque o guarda-corpo de tela do prop custa uma
 * barrinha a cada 16 cm: um tramo unico de 51 m seria mais de 600 pecas de
 * 2 cm num lugar onde ninguem repara na emenda.
 */
function buildCatwalk(level) {
  const b = level.batch;
  b.setRegion('dep_passarela');

  const x0 = MEZ_X1;
  const x1 = PLAT_X0;
  const segs = 4;
  const len = (x1 - x0) / segs;
  for (let i = 0; i < segs; i++) {
    const cx = x0 + len * (i + 0.5);
    level._place(P.makeCatwalk(level.mats, { len, w: 1.4 }), cx, CATWALK_Y, CATWALK_Z,
      Math.PI / 2);
    // pendural do teto: a passarela nao pode nascer do ar
    for (const sz of [-1, 1]) {
      level._slab('metal_thin', cx - 0.04, CATWALK_Y + 0.02, CATWALK_Z + sz * 0.66,
        cx + 0.04, H_HALL - 1.3, CATWALK_Z + sz * 0.72, { solid: false, cast: false });
    }
  }
  // apoio intermediario no piso, a cada tramo
  for (let i = 1; i < segs; i++) {
    const cx = x0 + len * i;
    level._slab('metal', cx - 0.14, Y_FLOOR, CATWALK_Z - 0.7, cx + 0.14, CATWALK_Y - 0.3,
      CATWALK_Z - 0.42);
    level._slab('metal', cx - 0.14, Y_FLOOR, CATWALK_Z + 0.42, cx + 0.14, CATWALK_Y - 0.3,
      CATWALK_Z + 0.7);
    level._slab('metal_thin', cx - 0.34, Y_FLOOR, CATWALK_Z - 0.9, cx + 0.34, 0.14,
      CATWALK_Z + 0.9, { solid: false });
  }

  // plataforma leste
  level._slab('metal', PLAT_X0, CATWALK_Y - 0.16, PLAT_Z0, PLAT_X1, CATWALK_Y, PLAT_Z1);
  level._slab('metal', PLAT_X0, CATWALK_Y - 0.42, PLAT_Z0, PLAT_X0 + 0.12, CATWALK_Y - 0.16,
    PLAT_Z1, { solid: false });
  for (const pz of [PLAT_Z0, PLAT_Z1]) {
    level._slab('metal', PLAT_X0 + 0.3, Y_FLOOR, pz + 0.1, PLAT_X0 + 0.54, CATWALK_Y - 0.16,
      pz + 0.34);
  }
  // guarda-corpo da plataforma, aberto onde chega a passarela e onde desce a escada
  level._place(P.makeRailing(level.mats, { len: PLAT_Z1 - PLAT_Z0 - 0.4, h: 1.05, mesh: true }),
    PLAT_X1 - 0.1, CATWALK_Y, (PLAT_Z0 + PLAT_Z1) * 0.5, Math.PI / 2);
  level._place(P.makeRailing(level.mats, { len: PLAT_X1 - PLAT_X0 - 2.6, h: 1.05, mesh: true }),
    PLAT_X0 + 1.3, CATWALK_Y, PLAT_Z1 - 0.1, 0);
  level._place(P.makeSandbagWall(level.mats, { len: 2.6, rows: 3, rng: P.makeRng(811) }),
    PLAT_X0 + 1.6, CATWALK_Y, PLAT_Z0 + 0.9, 0);

  // escada da plataforma para o corredor de servico (sobe em +Z)
  const steps = 13;
  const rise = CATWALK_Y / steps;
  const run = 0.34;
  level._place(P.makeStairs(level.mats, { steps, rise, run, w: 1.6, landing: 1.2 }),
    31.0, Y_FLOOR, PLAT_Z0 - (steps * run + 1.2), 0);

  // corredor de servico leste: tubulacao e quadros, para nao ser um vao vazio
  b.setRegion('dep_borda');
  for (let z = IN_Z0 + 3; z < IN_Z1 - 2; z += 7.5) {
    level._slab('metal', SRV_X0 + 0.4, 2.6, z - 0.16, IN_X1 - 0.1, 2.9, z + 0.16,
      { solid: false });
  }
  level._slab('metal_thin', IN_X1 - 0.38, 0.9, IN_Z0 + 2, IN_X1 - 0.06, 4.2, IN_Z1 - 2,
    { solid: false });
  for (let z = IN_Z0 + 6; z < IN_Z1 - 4; z += 9.0) {
    level._slab('metal_dull', IN_X1 - 0.5, 1.2, z, IN_X1 - 0.18, 2.1, z + 0.9, { solid: false });
  }
}

/**
 * Docas de caminhao na parede norte: plataforma alta, quatro portas de enrolar
 * fechadas, para-choques e as duas rampas que ligam a plataforma ao piso.
 */
function buildDocks(level) {
  const b = level.batch;
  b.setRegion('dep_doca');

  // plataforma alta - registrada depois do piso, entao _groundY devolve 1,05
  level._floor('concrete', IN_X0, DOCK_Z0, IN_X1, IN_Z1, Y_DOCK, 0.7);
  // nariz da plataforma, em borracha de impacto
  level._slab('rubber', IN_X0, Y_DOCK - 0.5, DOCK_Z0 - 0.12, IN_X1, Y_DOCK, DOCK_Z0,
    { solid: false, cast: false });

  // rampas de acesso
  stepRamp(level, 'z', DOCK_Z0, DOCK_Z0 - 4.0, -6.0, -2.0, Y_DOCK, Y_FLOOR, 4);
  stepRamp(level, 'z', DOCK_Z0, DOCK_Z0 - 4.0, 18.0, 22.0, Y_DOCK, Y_FLOOR, 4);
  // guarda-corpo da borda alta, poupando as duas rampas
  const runs = [[IN_X0 + 0.4, -6.6], [-1.4, 17.4], [22.6, IN_X1 - 0.4]];
  for (let i = 0; i < runs.length; i++) {
    const len = runs[i][1] - runs[i][0];
    if (len < 1.0) continue;
    level._place(P.makeRailing(level.mats, { len, h: 1.05, mesh: false }),
      (runs[i][0] + runs[i][1]) * 0.5, Y_DOCK, DOCK_Z0 + 0.25, 0);
  }

  // quatro baias: porta de enrolar fechada, nivelador e para-choques
  const bays = [-24, -8, 8, 24];
  for (let i = 0; i < bays.length; i++) {
    const bx = bays[i];
    // vao rebaixado na alvenaria, para a porta nao parecer colada
    level._slab('concrete', bx - 2.2, Y_DOCK, IN_Z1 - 0.12, bx + 2.2, Y_DOCK + 4.1,
      IN_Z1, { solid: false });
    level._place(P.makeRollUpDoor(level.mats, { w: 4.0, h: 3.9, open: i === 1 ? 0.35 : 0.0 }),
      bx, Y_DOCK, IN_Z1 - 0.16, Math.PI);
    for (const s of [-1, 1]) {
      level._slab('rubber', bx + s * 2.05, Y_DOCK - 0.62, IN_Z1 - 0.28, bx + s * 2.3,
        Y_DOCK - 0.12, IN_Z1, { solid: false, cast: false });
    }
    // nivelador de doca: chapa e a dobradica
    level._slab('metal_dull', bx - 1.8, Y_DOCK, IN_Z1 - 2.2, bx + 1.8, Y_DOCK + 0.07,
      IN_Z1 - 0.3, { cast: false });
    level._slab('metal', bx - 1.9, Y_DOCK - 0.2, IN_Z1 - 2.35, bx + 1.9, Y_DOCK,
      IN_Z1 - 2.16, { solid: false });
    // sinalizacao da baia no piso
    level._slab('asphalt_line', bx - 1.9, Y_DOCK, IN_Z1 - 5.6, bx - 1.7, Y_DOCK + 0.012,
      IN_Z1 - 2.4, { solid: false, cast: false });
    level._slab('asphalt_line', bx + 1.7, Y_DOCK, IN_Z1 - 5.6, bx + 1.9, Y_DOCK + 0.012,
      IN_Z1 - 2.4, { solid: false, cast: false });
  }

  // material de expedicao na plataforma - cobertura no unico vao grande do mapa
  const rng = P.makeRng(9292);
  const spots = [[-30, 27.5], [-16, 26.5], [-2, 28.5], [14, 26.8], [30, 28.0], [4, 31.0],
    [-22, 30.6], [20, 30.8]];
  for (let i = 0; i < spots.length; i++) {
    const sx = spots[i][0];
    const sz = spots[i][1];
    if (rng() < 0.5) {
      level._place(P.makePallet(level.mats), sx, Y_DOCK, sz, rng() * 3.1);
      for (let k = 0; k < 2; k++) {
        level._place(P.makeCrate(level.mats, { variant: (i + k) % 4 }),
          sx, Y_DOCK + 0.11 + k * 0.74, sz, P.rand(rng, -0.4, 0.4));
      }
    } else {
      level._place(P.makeTarpStack(level.mats, { count: 3, rng: P.makeRng(930 + i) }),
        sx, Y_DOCK, sz, rng() * 0.8);
    }
  }
  level._place(P.makeDumpster(level.mats, {}), -12.0, Y_DOCK, 31.2, 0.1);
  level._place(P.makeJerseyBarrier(level.mats, { len: 2.4 }), 26.0, Y_DOCK, 25.4, 0);
  level._interiorDust(IN_X0, DOCK_Z0, IN_X1, IN_Z1, Y_DOCK, 1.2);
}

/** Sala de maquinas fechada, com laje acessivel por escada externa. */
function buildMachineRoom(level) {
  const b = level.batch;
  b.setRegion('dep_sul');
  const t = 0.3;

  level._slab('concrete', MAQ_X0, Y_FLOOR, IN_Z0, MAQ_X0 + t, MAQ_H, MAQ_Z1);
  level._slab('concrete', MAQ_X1 - t, Y_FLOOR, IN_Z0, MAQ_X1, MAQ_H, MAQ_Z1);
  level._slab('concrete', MAQ_X0, Y_FLOOR, IN_Z0, MAQ_X1, MAQ_H, IN_Z0 + t);
  // face norte com porta e uma veneziana
  level._slab('concrete', MAQ_X0, Y_FLOOR, MAQ_Z1 - t, 11.4, MAQ_H, MAQ_Z1);
  level._slab('concrete', 11.4, Y_FLOOR + 2.25, MAQ_Z1 - t, 12.8, MAQ_H, MAQ_Z1);
  level._slab('concrete', 12.8, Y_FLOOR, MAQ_Z1 - t, MAQ_X1, MAQ_H, MAQ_Z1);
  level._place(P.makeDoorUnit(level.mats, { w: 1.3, h: 2.2, style: 'metal', ajar: 0.45, open: true }),
    12.1, Y_FLOOR, MAQ_Z1 - t * 0.5, 0);
  for (let i = 0; i < 7; i++) {
    level._slab('metal_dull', 15.2, Y_FLOOR + 1.1 + i * 0.16, MAQ_Z1 - 0.06, 18.4,
      Y_FLOOR + 1.2 + i * 0.16, MAQ_Z1 + 0.04, { solid: false, cast: false });
  }
  // laje
  level._slab('concrete', MAQ_X0 - 0.14, MAQ_H, IN_Z0, MAQ_X1 + 0.14, MAQ_TOP, MAQ_Z1 + 0.14);
  level._place(P.makeRailing(level.mats, { len: MAQ_X1 - MAQ_X0 - 0.6, h: 1.0, mesh: true }),
    (MAQ_X0 + MAQ_X1) * 0.5, MAQ_TOP, MAQ_Z1 - 0.1, 0);
  level._place(P.makeSandbagWall(level.mats, { len: 3.0, rows: 3, rng: P.makeRng(611) }),
    MAQ_X1 - 1.6, MAQ_TOP, -29.5, Math.PI / 2);
  level._place(P.makeWaterTank(level.mats, { r: 0.9, h: 1.6 }), MAQ_X0 + 2.2, MAQ_TOP, -30.5, 0.4);
  level._place(P.makeAC(level.mats, {}), MAQ_X0 + 5.0, MAQ_TOP + 0.34, -28.0, 0);
  level._place(P.makeRoofVent(level.mats, {}), MAQ_X1 - 4.0, MAQ_TOP, -31.0, 0);

  // escada externa, subindo para LESTE
  const steps = 10;
  const rise = MAQ_TOP / steps;
  const run = 0.34;
  level._place(P.makeStairs(level.mats, { steps, rise, run, w: 1.6, landing: 1.2 }),
    MAQ_X0 - (steps * run + 1.2), Y_FLOOR, -28.5, Math.PI / 2);

  // interior: compressores e bancada
  level._place(P.makeWorkbench(level.mats, { w: 2.4 }), 17.6, Y_FLOOR, -31.6, 0);
  for (let i = 0; i < 3; i++) {
    level._place(P.makeWaterTank(level.mats, { r: 0.62, h: 1.5, legs: false }),
      8.2 + i * 1.7, Y_FLOOR, -31.2, 0.2);
  }
  level._place(P.makeHangingLamp(level.mats, { len: 0.7 }), 13.0, MAQ_H - 0.1, -29.0, 0);
  level._interiorDust(MAQ_X0, IN_Z0, MAQ_X1, MAQ_Z1, Y_FLOOR, 0.9);
}

/** Pintura de piso, sujeira e a tralha do vao sul. Nada disso colide. */
function buildFloorDetail(level) {
  const b = level.batch;
  b.setRegion('dep_piso');
  const line = { solid: false, cast: false };

  // faixa amarela dos corredores
  for (let i = 0; i < RACK_Z.length; i++) {
    const z = RACK_Z[i] + 3.75;
    if (z > IN_Z1 - 1.5) continue;
    level._slab('asphalt_line', IN_X0 + 1.0, Y_FLOOR, z - 0.08, IN_X1 - 1.0,
      Y_FLOOR + 0.012, z + 0.08, line);
  }
  for (let k = 0; k < CROSS_X.length; k++) {
    level._slab('asphalt_line', CROSS_X[k] - 0.08, Y_FLOOR, IN_Z0 + 1.0, CROSS_X[k] + 0.08,
      Y_FLOOR + 0.012, DOCK_Z0 - 0.5, line);
  }
  // area de espera hachurada, na boca das transversais
  for (let k = 0; k < CROSS_X.length; k++) {
    for (let i = 0; i < 6; i++) {
      const x = CROSS_X[k] - 1.4 + i * 0.5;
      level._slab('asphalt_line', x, Y_FLOOR, -24.4, x + 0.14, Y_FLOOR + 0.012, -22.6, line);
    }
  }

  // manchas de oleo e poeira
  const rng = P.makeRng(3737);
  for (let i = 0; i < 22; i++) {
    const px = P.rand(rng, IN_X0 + 2, IN_X1 - 2);
    const pz = P.rand(rng, IN_Z0 + 2, IN_Z1 - 2);
    const r = P.rand(rng, 0.5, 1.7);
    level._slab('dirt', px - r, Y_FLOOR, pz - r * 0.8, px + r, Y_FLOOR + 0.01, pz + r * 0.8, line);
  }
  level._interiorDust(IN_X0, IN_Z0, IN_X1, DOCK_Z0, Y_FLOOR, 1.3);

  // vao sul: area de recebimento com tralha de verdade
  b.setRegion('dep_sul');
  for (let i = 0; i < 4; i++) {
    level._place(P.makeShelf(level.mats, { w: 2.4, h: 2.3, d: 0.8 }),
      -30.0 + i * 0.0, Y_FLOOR, -30.4 + i * 2.6, Math.PI / 2);
  }
  const spots = [[-24, -28.5], [-18, -31.0], [-12, -28.0], [-4, -30.5], [0, -27.0],
    [24, -30.0], [28, -27.0], [-28, -24.5]];
  for (let i = 0; i < spots.length; i++) {
    const sx = spots[i][0];
    const sz = spots[i][1];
    const r = rng();
    if (r < 0.4) {
      level._place(P.makePallet(level.mats), sx, Y_FLOOR, sz, rng() * 3.1);
      level._place(P.makeCrate(level.mats, { variant: i % 4 }), sx, Y_FLOOR + 0.11, sz,
        rng() * 3.1);
    } else if (r < 0.7) {
      for (let k = 0; k < 3; k++) {
        level._place(P.makeBarrel(level.mats, { variant: k === 1 ? 1 : 0 }),
          sx + P.rand(rng, -0.6, 0.6), Y_FLOOR, sz + P.rand(rng, -0.6, 0.6), rng() * 3.1);
      }
    } else {
      level._place(P.makeTarpStack(level.mats, { count: 2, rng: P.makeRng(950 + i) }),
        sx, Y_FLOOR, sz, rng() * 0.9);
    }
  }
  level._place(P.makeDumpster(level.mats, {}), 3.0, Y_FLOOR, -31.4, 0);
  level._place(P.makeWorkbench(level.mats, { w: 2.6 }), -21.0, Y_FLOOR, -24.8, Math.PI);
  for (let k = 0; k < 5; k++) {
    level._place(P.makeTire(level.mats, {}), 22.4, Y_FLOOR + k * 0.19, -24.8, rng() * 3.1);
  }
}

/**
 * O que se ve pelas claraboias e pela porta de doca meio aberta: ceu e duas
 * massas distantes. Fora do galpao nao existe jogo, entao e tudo 'backdrop' sem
 * colisao, na regiao 'fundo'.
 */
function buildOutside(level) {
  const b = level.batch;
  b.setRegion('fundo');
  const off = { solid: false, cast: false };
  const masses = [
    [-72, 46, -30, 74, 18], [6, 44, 52, 70, 14], [58, -20, 92, 24, 22],
    [-92, -18, -58, 26, 16], [-40, -80, 6, -50, 20]
  ];
  for (let i = 0; i < masses.length; i++) {
    const m = masses[i];
    b.setPhase(i * 23 + 11);
    level._slab('backdrop', m[0], 0, m[1], m[2], m[4], m[3], off);
    b.clearVariant();
    level._slab('concrete', m[0] - 0.3, m[4], m[1] - 0.3, m[2] + 0.3, m[4] + 0.8, m[3] + 0.3, off);
  }
  // duas chamines: a unica coisa que aparece por cima da cumeeira
  for (let k = 0; k < 2; k++) {
    const cx = k === 0 ? -46 : 54;
    const cz = k === 0 ? 58 : -34;
    level._slab('brick', cx - 1.4, 0, cz - 1.4, cx + 1.4, 34 + k * 8, cz + 1.4, off);
    level._slab('concrete', cx - 1.7, 34 + k * 8, cz - 1.7, cx + 1.7, 35.2 + k * 8, cz + 1.7, off);
  }
  b.setRegion('dep_piso');
}

// ---------------------------------------------------------------------------
// nascimentos e pontos de vista
// ---------------------------------------------------------------------------

/**
 * Todos os pontos ficam nas quatro areas livres (vao sul, plataforma de doca,
 * corredor de servico leste e o vao sob o mezanino) ou nas duas transversais -
 * nunca num corredor de prateleira, onde as cargas de bloqueio andam. Os cinco
 * pontos altos passam cota explicita: _groundY so conhece piso registrado por
 * _floor, e laje de mezanino nao e piso.
 */
export function spawnPoints(level) {
  return {
    player: [
      level._spawn(-28.0, -28.5),                 // recebimento, canto sudoeste
      level._spawn(-6.0, -30.0),                  // recebimento, centro
      level._spawn(26.0, -29.5),                  // recebimento, canto sudeste
      level._spawn(31.4, -10.0),                  // corredor de servico leste
      level._spawn(31.4, 14.0),                   // corredor de servico, norte
      level._spawn(CROSS_X[0], -14.0),            // transversal oeste
      level._spawn(CROSS_X[0], 8.0),              // transversal oeste, norte
      level._spawn(-28.0, 0.0)                    // sob o mezanino
    ],
    enemy: [
      level._spawn(-24.0, 29.0),                  // plataforma de doca, oeste
      level._spawn(0.0, 29.5),                    // plataforma de doca, centro
      level._spawn(24.0, 29.0),                   // plataforma de doca, leste
      level._spawn(CROSS_X[1], 16.0),             // transversal leste
      level._spawn(CROSS_X[1], -8.0),             // transversal leste, sul
      level._spawn(-29.0, 20.0),                  // sob o escritorio
      level._spawn(-28.5, 6.0, MEZ_Y + 0.08),     // mezanino, meio
      level._spawn(-28.5, -14.0, MEZ_Y + 0.08),   // mezanino, sul
      level._spawn(13.0, -29.0, MAQ_TOP + 0.08),  // laje da sala de maquinas
      level._spawn(29.5, 1.0, CATWALK_Y + 0.08)   // plataforma da passarela
    ]
  };
}

/** Ver a nota de `pos`/`position` em porto.js: e o mesmo array, dois nomes. */
function vp(out, name, fx, fy, fz, tx, ty, tz) {
  const dx = tx - fx;
  const dy = ty - fy;
  const dz = tz - fz;
  const yaw = Math.round((Math.atan2(-dx, -dz) * 180) / Math.PI * 10) / 10;
  const pitch = Math.round((Math.atan2(dy, Math.hypot(dx, dz)) * 180) / Math.PI * 10) / 10;
  const pos = [fx, fy, fz];
  out.push({ name, pos, position: pos, yaw, pitch });
}

export function viewpoints(level) {
  const eye = 1.65;
  const out = [];
  vp(out, 'dep_corredor_estoque', -22.0, eye, RACK_Z[2] + 3.75, 26.0, 1.4, RACK_Z[2] + 3.75);
  vp(out, 'dep_transversal', CROSS_X[0], eye, -22.0, CROSS_X[0], 3.0, 22.0);
  vp(out, 'dep_passarela', -20.0, CATWALK_Y + eye, CATWALK_Z, 26.0, 3.0, CATWALK_Z);
  vp(out, 'dep_mezanino', -26.0, MEZ_Y + eye, -12.0, 10.0, 1.0, 6.0);
  vp(out, 'dep_escritorio', -27.0, MEZ_Y + eye, 14.0, 20.0, 2.0, 6.0);
  vp(out, 'dep_docas', 0.0, Y_DOCK + eye, 27.0, 2.0, 3.4, 32.4);
  vp(out, 'dep_doca_para_hall', 6.0, Y_DOCK + eye, 25.0, -14.0, 1.2, -6.0);
  vp(out, 'dep_maquinas_laje', 13.0, MAQ_TOP + eye, -29.0, 0.0, 1.2, 6.0);
  vp(out, 'dep_servico_leste', 31.4, eye, -20.0, 31.4, 3.6, 20.0);
  vp(out, 'dep_claraboia', 0.0, eye, -6.0, 2.0, 12.0, 4.0);
  vp(out, 'dep_plano_alto', -28.0, 9.4, -26.0, 12.0, 1.0, 14.0);
  vp(out, 'dep_prateleira_close', 8.0, 1.5, RACK_Z[3] - 2.2, 8.0, 1.6, RACK_Z[3] - 0.8);
  return out;
}
